import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import {getUserToken} from "../utils/helper";
import {stringify} from "qs";
import {settings} from "@/Settings";
import {router} from "@/router";
import {eventBus} from "@/common/eventBus";
import {setLicenseExpiredActive} from "@/common/licenseExpiredState";

let responseInterceptorId = null;

const ApiService = {
    init() {
        Vue.use(VueAxios, axios);

        Vue.axios.defaults.baseURL = process.env.VUE_APP_API_SERVER || settings.API_SERVER;
    },
    setHeader() {
        const token = getUserToken();
        if (token) {
            Vue.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        } else {
            delete Vue.axios.defaults.headers.common["Authorization"];
        }

        if (responseInterceptorId !== null) {
            Vue.axios.interceptors.response.eject(responseInterceptorId);
        }

        responseInterceptorId = Vue.axios.interceptors.response.use(
            function (response) {
                return response;
            },
            function (error) {
                console.error(error);
                const status = error?.response?.status;
                const message = (error?.response?.data?.message || "").toString();
                const isLicenseExpired = message.toLowerCase().includes("license expired");
                if (isLicenseExpired || status === 423) {
                    setLicenseExpiredActive(true);
                    eventBus.$emit(
                        "license-expired",
                        message || "License expired. Please renew license to use the functionality."
                    );
                    return Promise.reject(error);
                }
                if (status === 401) {
                    router.push("/login");
                }
                return Promise.reject(error);
            }
        );
    },
    query(resource, params) {
        return Vue.axios
            .get(resource, {
                params: params,
                paramsSerializer: (params) => {
                    return stringify(params, {arrayFormat: "repeat"});
                },
            })
            .catch((error) => {
                throw new Error(`[RWV] ApiService ${error}`);
            });
    },

    get(resource) {
        return Vue.axios.get(`${resource}`).catch((error) => {
            throw new Error(`[RWV] ApiService ${error}`);
        });
    },

    post(resource, params) {
        return Vue.axios.post(`${resource}`, params);
    },

    postFormData(resource, params) {
        return Vue.axios.post(`${resource}`, params, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
    },

    updateFormData(resource, slug, params) {
        return Vue.axios.put(`${resource}/${slug}`, params, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
    },

    update(resource, slug, params) {
        return Vue.axios.put(`${resource}/${slug}`, params);
    },

    put(resource, params) {
        return Vue.axios.put(`${resource}`, params);
    },

    delete(resource, slug, params) {
        return Vue.axios.delete(`${resource}/${slug}`, params);
    },

    patch(resource, params) {
        return Vue.axios.patch(`${resource}`, params);
    },

    patchBySlug(resource, slug, params) {
        return Vue.axios.patch(`${resource}/${slug}`, params);
    },

    getWithConfig(resource, config = {}) {
        return Vue.axios.get(`${resource}`, config);
    },
};

export default ApiService;

export const UserService = {
    login(params) {
        return ApiService.post("/user/login", params);
    },
};
export const LookupService = {
    getGraduateTypes() {
        return ApiService.get("/graduateTypes");
    },
    getTrainingCenters() {
        return ApiService.query("/trainingCenter");
    },
    getHealthProffesionsWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/healthProfessions?page=${page}&pageSize=${itemsPerPage}&name=${name}`);
    },
    getHealthProffesions() {
        return ApiService.get("/healthProfessions");
    },
    getCourses() {
        return ApiService.get("/courses/drp");
    },
    getProgramTypes() {
        return ApiService.get("/programTypes");
    },
    getCourseLevel() {
        return ApiService.get("/courseLevels");
    },
    getTrainerTypes() {
        return ApiService.get("/trainerTypes");
    },
    getPrograms() {
        return ApiService.get("/programs");
    },
    getProgramsLite() {
        return ApiService.get("/programs?lite=1");
    },
    getProgramsDrp(id) {
        const url = id ? `/programs/drps?categoryId=${id}` : `/programs/drps`;
        return ApiService.get(url);
    },
    getTrainers() {
        return ApiService.get("/Trainers");
    },
    getTrainees() {
        return ApiService.get("/trainees");
    },
    getProfession() {
        return ApiService.get("/professions");
    },
    getEducationalLevel() {
        return ApiService.get("/educationalLevels");
    },
    getCategories() {
        return ApiService.get("/category");
    },
    getCategoriesWP(page, itemsPerPage, name, categoryType) {
        return ApiService.get(
            `/category/wp?page=${page}&pageSize=${itemsPerPage}&name=${name}&categoryType=${categoryType || ""}`
        );
    },
    getRegion() {
        // return ApiService.get("/location");
        return ApiService.get("/mfr/regions/drp");
    },
    getTrainerSelectionCriteriaWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/trainerSelectionCriteria?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getTrainerSelectionCriteria() {
        return ApiService.get("/trainerSelectionCriteria");
    },
    getWorkExperience() {
        return ApiService.get("/workExperience");
    },
    getTrainingCategories() {
        return ApiService.get("/trainingCategory");
    },
    getLevelOfStudies() {
        return ApiService.get("/levelOfStudies");
    },
    getLevelOfStudiesWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/levelOfStudies?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getGeneralTimes() {
        return ApiService.get("/general-times");
    },
    getAccreditedBy() {
        return ApiService.get("/accreditedBy");
    },
    getCourseOwners() {
        return ApiService.get("/courseOwners");
    },
    getAudiences() {
        return ApiService.get("/targetAudiences");
    },
    getTrainingCourseUsed() {
        return ApiService.get("/trainingCourseUsed");
    },
    getFoundedBy() {
        return ApiService.get("/fundedBy");
    },
    getOrganizations(type = "") {
        const query = type ? `?type=${encodeURIComponent(type)}` : "";
        return ApiService.get(`/organizations${query}`);
    },
    getOrganizationsWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/organizations?page=${page}&pageSize=${itemsPerPage}&name=${encodeURIComponent(name || "")}`
        );
    },
    getActivityDeliveries() {
        return ApiService.get("/activityDeliveries");
    },
    getCpdProviders() {
        return ApiService.get("/trainingCenters");
    },
    getZones() {
        return ApiService.get("/zones");
    },
    getZoneByRegionId(regionId) {
        let url = regionId ? `/mfr/zones/drp?regionId=${regionId}` : "/mfr/zones/drp";
        return ApiService.get(url);
    },
    getWoredaByZoneId(zoneId) {
        let url = zoneId ? `/mfr/woredas/drp?zoneId=${zoneId}` : "/mfr/woredas/drp";
        return ApiService.get(url);
    },
    getFacilityByWoredaId(woredaId) {
        let url = woredaId ? `/mfr/facilities/drp?woredaId=${woredaId}` : "/mfr/facilities/drp";
        return ApiService.get(url);
    },
    getWoredas() {
        return ApiService.get("/woredas");
    },
    getExpertLevels() {
        return ApiService.get("/expertLevels");
    },
    getFacilityTypes() {
        return ApiService.get("/facilityTypes");
    },
    getUserFacilities(locationId, facilityTypeId) {
        return ApiService.get(
            "/facilities/" + `${locationId}` + "/" + `${facilityTypeId}`
        );
    },
    getAllUserFacilitiesWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/facilities?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getAllUserFacilities() {
        return ApiService.get("/facilities");
    },
    getAccredatorOwnership() {
        return ApiService.get("/accredatorOwnership");
    },
    getNewTraineeFacilities() {
        return ApiService.get("/newTraineeFacilities");
    },
    getTraineeFacilities() {
        return ApiService.get("/traineeFacilities");
    },
    traineeFacilitiesLookup(page, itemsPerPage, name) {
        return ApiService.get(
            `/traineeFacilities/traineeFacilitiesLookup?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getUniversityOwnership() {
        return ApiService.get("/universityOwnership");
    },
    getGenerationLevels() {
        return ApiService.get("/generationLevel");
    },
    getUniversities() {
        return ApiService.get("/universities");
    },
    getUniversitiesWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/universities?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getCertReasons() {
        return ApiService.get("/certificateReasons");
    },
    getCertReasonsWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/certificateReasons?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getStudentStatuses() {
        return ApiService.get("/studentStatuses");
    },
    getEthnicities() {
        return ApiService.get("/ethnicities");
    },
    getEthnicitiesWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/ethnicities?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getEducationalPrograms() {
        return ApiService.get("/educationalPrograms");
    },
    getEducationalProgramsWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/educationalPrograms?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getDisciplines() {
        return ApiService.get("/disciplines");
    },
    getDisciplinesWP(page, itemsPerPage, name) {
        return ApiService.get(
            `/disciplines?page=${page}&pageSize=${itemsPerPage}&name=${name}`
        );
    },
    getCatIIRequirements() {
        return ApiService.get("/catIIRequirement");
    },
    getCatIIRequirementsByCategory(cpdCategoryId) {
        if (!cpdCategoryId) {
            return ApiService.get("/catIIRequirement");
        }
        return ApiService.get(`/catIIRequirement?cpdCategoryId=${cpdCategoryId}`);
    },
    getCatIIRequirementWP(page, itemsPerPage, role) {
        return ApiService.get(
            `/catIIRequirement?page=${page}&pageSize=${itemsPerPage}&role=${role}`
        );
    },
};
